import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { Chip, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from 'moment';
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { tableConfig } from "../../../commons/table/tableConfig";
import { StyledResolucion } from "../../../styled/Prevencion";

const ExamenesMedicosTable = ({ data, handleOpenExamenesMedicos, sustancias, values }) => {

  const tiposExamenesMedicos = useSelector((state) => state.resolucion.tiposExamenesMedicos.data);

  const columns = useMemo(() => [
    {
      id: "sustancia",
      header: "Sustancia",
      size: 200,
      Cell: ({ row }) => {
        const s = sustancias.find((s) => s.id === row.original.id);
        return `${s?.sustancia?.descripcionAgente ?? ""} - ${s?.sustancia?.nombreAgente ?? ""}`;
      },
    },
    {
      id: "inicioContacto",
      header: "Inicio Exposición",
      size: 120,
      Cell: ({ row }) => {
        const date = values?.examenes?.[row.original.id]?.inicioContacto || values?.inicioContacto;
        return date ? moment(date).format("DD/MM/YYYY") : "-";
      },
    },
    {
      id: "finContacto",
      header: "Fin Exposición",
      size: 120,
      Cell: ({ row }) => {
        const date = values?.examenes?.[row.original.id]?.finContacto || values?.finContacto;
        return date ? moment(date).format("DD/MM/YYYY") : "-";
      },
    },
    {
      id: "examenesRealizados",
      header: "Exámenes Realizados",
      enableColumnFilter: false,
      size: 350,
      Cell: ({ row }) => {
        const sustanciaId = row.original.id;
        const examData = values?.examenes?.[sustanciaId];

        if (!examData) {
          return (
            <Chip
              label="Sin informar"
              size="small"
              variant="outlined"
              color="default"
              sx={{ fontSize: '0.75rem' }}
            />
          );
        }

        const realizados = tiposExamenesMedicos
          .filter((tipo) => examData[tipo.id]?.value)
          .map((tipo) => tipo.nombre);

        if (realizados.length === 0) {
          return (
            <Chip
              label="Sin informar"
              size="small"
              variant="outlined"
              color="default"
              sx={{ fontSize: '0.75rem' }}
            />
          );
        }

        return realizados.map((label) => (
          <Chip
            key={label}
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label={label}
            size="small"
            variant="outlined"
            sx={{ mr: 0.5, mb: 0.25, fontSize: '0.7rem' }}
          />
        ));
      },
    },
  ], [sustancias, values?.examenes, tiposExamenesMedicos]);

  const table = useMaterialReactTable({
    columns,
    data: data || [],
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "Informar",
        size: 80,
        muiTableHeadCellProps: {
          sx: {
            ...tableConfig.muiTableHeadCellProps?.sx,
            justifyContent: 'center',
          },
        }
      },
    },
    enableHiding: false,
    paginationDisplayMode: 'pages',
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20],
      labelRowsPerPage: "Filas por página:",
      getItemAriaLabel: (type) => {
        if (type === 'first') return 'ir a la primera página';
        if (type === 'last') return 'ir a la última página';
        if (type === 'next') return 'ir a la página siguiente';
        return 'ir a la página anterior';
      },
      labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
    },
    localization: {
      ...MRT_Localization_ES,
      noRecordsToDisplay: "No hay datos que mostrar",
    },
    renderTopToolbarCustomActions: () => (
      <StyledResolucion.HeaderContainer>
        <StyledResolucion.TableTitle>
          Agentes de Riesgos Cancerígenos al que el Trabajador esta Expuesto
        </StyledResolucion.TableTitle>
      </StyledResolucion.HeaderContainer>
    ),
    renderRowActions: ({ row }) => (
      <Tooltip title="Informar Exámenes">
        <IconButton
          onClick={() => handleOpenExamenesMedicos(row.original, values)}
          color="primary"
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    ),
    enablePagination: true,
    enableColumnFilters: true,
    enableFilters: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    muiTableHeadCellProps: {
      sx: { ...tableConfig.muiTableHeadCellProps?.sx }
    },
    muiTableBodyRowProps: {
      sx: { ...tableConfig.muiTableBodyRowProps?.sx }
    },
    muiFilterTextFieldProps: {
      placeholder: 'Filtrar...',
      size: 'small',
      variant: 'outlined',
    },
  });

  return (
    <StyledResolucion.TableContainer>
      <MaterialReactTable table={table} />
    </StyledResolucion.TableContainer>
  );
};

export default ExamenesMedicosTable;